import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@astro/api'

/**
 * The client half of an astrologer's live stream: the session (join →
 * heartbeat → leave) and the chat poll.
 *
 * Why hooks rather than React Query: joining has an effect server-side (it
 * opens a watch session and is what the audience count is built from), and the
 * heartbeat is a timer, not a cache read. Only the public list and the stream
 * card below are plain queries — those live in the pages.
 *
 * The one rule to keep in mind while reading this: nothing here requires a
 * login. A signed-out visitor joins, watches and reads the chat exactly like
 * anyone else, identified only by the opaque `viewer_key` the server issued.
 * Posting a message is the single call that needs an account, and the server
 * says so in the session (`can_chat` / `requires_login_for_chat`) rather than
 * leaving the UI to guess.
 */

/** The viewer_key for one stream, kept per tab.
 *
 *  sessionStorage, not state: a reload must reuse the same key so the server
 *  revives that watch session instead of counting a second viewer. Per tab
 *  rather than per browser (localStorage) because two tabs really are two
 *  viewers - and sharing the key would have them evict each other's identity
 *  on the SFU. */
const viewerKeyFor = (streamId) => `live-stream-viewer:${streamId}`

function readViewerKey(streamId) {
  try {
    return window.sessionStorage.getItem(viewerKeyFor(streamId)) || undefined
  } catch {
    return undefined
  }
}

function writeViewerKey(streamId, key) {
  try {
    window.sessionStorage.setItem(viewerKeyFor(streamId), key)
  } catch {
    /* private mode - the session just won't survive a reload */
  }
}

/**
 * Join a stream and stay joined.
 *
 * Phases: 'connecting' → 'ready' | 'ended' | 'missing' | 'error'. 'ended' is a
 * normal outcome, not a failure - a shared link to a finished broadcast lands
 * here, and so does a viewer still on the page when the astrologer stops.
 *
 * `isHost` comes from the server, never from the page: send an astrologer's own
 * session and the join answers with a publisher payload.
 */
export function useLiveStreamSession(streamId, { enabled = true } = {}) {
  const [session, setSession] = useState(null)
  const [phase, setPhase] = useState('connecting')
  const [error, setError] = useState(null)
  const [live, setLive] = useState({ isLive: true, viewerCount: 0, chatEnabled: true })

  // Guards a double join in React's development double-effect. (Even if one
  // slips through, the server dedupes on the viewer_key.)
  const joinedRef = useRef(null)
  const sessionRef = useRef(null)

  useEffect(() => {
    if (!enabled || !streamId) return
    const attempt = `${streamId}`
    if (joinedRef.current === attempt) return
    joinedRef.current = attempt

    setPhase('connecting')
    setError(null)

    // Why this is keyed on `joinedRef` and not on a per-run `cancelled` flag:
    // StrictMode runs this effect twice. The guard above correctly stops the
    // second run from opening a second watch session - but that means the
    // FIRST run owns the only request in flight, and its cleanup has already
    // fired by the time the response lands. A `cancelled` flag would throw
    // that response away and leave the page on "Connecting..." for ever.
    // Checking the ref instead discards a response only when a genuinely
    // newer attempt has superseded it.
    const current = () => joinedRef.current === attempt

    api.liveStreams
      .join(streamId, readViewerKey(streamId))
      .then((res) => {
        if (!current()) return
        const next = res?.data
        if (!next?.token) {
          setPhase('error')
          setError('The server did not return a usable session.')
          return
        }
        if (next.viewer_key) writeViewerKey(streamId, next.viewer_key)
        sessionRef.current = next
        setSession(next)
        setLive({
          isLive: Boolean(next.live_stream?.is_live),
          viewerCount: Number(next.live_stream?.viewer_count) || 0,
          chatEnabled: Boolean(next.chat_enabled),
        })
        setPhase('ready')
      })
      .catch((err) => {
        if (!current()) return
        // 409 = the broadcast is over; 404 = no such stream. Neither is an
        // error worth a retry or a scary message.
        if (err?.status === 409) {
          setPhase('ended')
        } else if (err?.status === 404) {
          setPhase('missing')
        } else {
          setPhase('error')
          setError(err?.message || 'Could not connect to this live stream.')
        }
      })

    // No cleanup: there is nothing to cancel that `joinedRef` does not
    // already supersede, and tearing the join down here is exactly what
    // stranded the page on "Connecting...". Leaving the stream is handled by
    // its own unmount effect below.
  }, [streamId, enabled])

  // Heartbeat: keeps this viewer in the audience count (or, for the host, keeps
  // the broadcast from being reaped as abandoned) and doubles as the state poll
  // - it is what notices the astrologer ending the stream, or an admin
  // switching the chat off.
  useEffect(() => {
    if (phase !== 'ready' || !session) return
    const seconds = Number(session.heartbeat_seconds) || 30
    const key = session.is_host ? undefined : session.viewer_key

    let stopped = false
    const beat = () => {
      api.liveStreams
        .heartbeat(streamId, key)
        .then((res) => {
          if (stopped) return
          const data = res?.data
          if (!data) return
          setLive({
            isLive: Boolean(data.is_live),
            viewerCount: Number(data.viewer_count) || 0,
            chatEnabled: Boolean(data.chat_enabled),
          })
          if (!data.is_live) setPhase('ended')
        })
        .catch(() => {
          /* a missed heartbeat is survivable - the window is wider than one */
        })
    }

    const timer = window.setInterval(beat, seconds * 1000)
    beat()
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [phase, session, streamId])

  // Tell the server the player is closing so the count drops for everyone else
  // at once. Fire-and-forget: presence expires on its own if this never lands.
  useEffect(() => {
    return () => {
      const current = sessionRef.current
      if (current && !current.is_host && current.viewer_key) {
        api.liveStreams.leave(streamId, current.viewer_key).catch(() => {})
      }
    }
  }, [streamId])

  return {
    session,
    phase,
    error,
    isHost: Boolean(session?.is_host),
    isLive: live.isLive,
    viewerCount: live.viewerCount,
    chatEnabled: live.chatEnabled,
    /** Called by the stages when the host ends the broadcast locally. */
    markEnded: useCallback(() => setPhase('ended'), []),
  }
}

/**
 * The chat for one stream.
 *
 * Reading is open to everyone, so this polls regardless of who is watching.
 * `after_id` makes each poll carry only what is new; the first load
 * deliberately returns the most recent page rather than the oldest messages.
 */
export function useLiveStreamChat(streamId, { enabled = true, intervalMs = 4000 } = {}) {
  const [messages, setMessages] = useState([])
  const [meta, setMeta] = useState({ chatEnabled: true, canChat: false, requiresLogin: true })
  const [sendError, setSendError] = useState(null)
  const [sending, setSending] = useState(false)
  const lastIdRef = useRef(0)

  useEffect(() => {
    if (!enabled || !streamId) return
    let stopped = false

    const poll = () => {
      api.liveStreams
        .chat(streamId, lastIdRef.current ? { after_id: lastIdRef.current } : {})
        .then((res) => {
          if (stopped) return
          const data = res?.data
          if (!data) return
          setMeta({
            chatEnabled: Boolean(data.chat_enabled),
            canChat: Boolean(data.can_chat),
            requiresLogin: Boolean(data.requires_login_for_chat),
          })
          const incoming = data.messages ?? []
          if (incoming.length === 0) return
          lastIdRef.current = Math.max(lastIdRef.current, ...incoming.map((m) => m.id))
          setMessages((prev) => {
            // A moderated message vanishes from later reads but is already in
            // `prev`; de-duplicating by id is enough for the growth case, and
            // dropping it from the screen on the next full reload is the
            // behaviour the API documents.
            const seen = new Set(prev.map((m) => m.id))
            return [...prev, ...incoming.filter((m) => !seen.has(m.id))]
          })
        })
        .catch(() => {})
    }

    const timer = window.setInterval(poll, intervalMs)
    poll()
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [streamId, enabled, intervalMs])

  const send = useCallback(
    async (text) => {
      const message = String(text || '').trim()
      if (!message) return false
      setSending(true)
      setSendError(null)
      try {
        const res = await api.liveStreams.sendMessage(streamId, message)
        const sent = res?.data
        if (sent?.id) {
          lastIdRef.current = Math.max(lastIdRef.current, sent.id)
          setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]))
        }
        return true
      } catch (err) {
        // 401 is the whole "sign in to interact" rule arriving from the server.
        if (err?.status === 401) {
          setMeta((prev) => ({ ...prev, canChat: false, requiresLogin: true }))
          setSendError('Please sign in to join the chat.')
        } else if (err?.status === 429) {
          setSendError('Slow down a moment before sending again.')
        } else {
          setSendError(err?.message || 'Could not send your message.')
        }
        return false
      } finally {
        setSending(false)
      }
    },
    [streamId],
  )

  return { messages, ...meta, send, sending, sendError, clearSendError: () => setSendError(null) }
}

/* The number formatting lives in @astro/api (liveStreamViewerLabel and
   friends), because the live bands on BOTH home pages render the same
   figures and two copies would drift. Re-exported here under the shorter
   names this folder already uses. */
export {
  liveStreamViewerLabel as viewerLabel,
  liveStreamElapsed as elapsedLabel,
  liveStreamAstrologerName as astrologerName,
} from '@astro/api'
